import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_BRAND = "#22C55E";

function clamp(x) { return Math.max(0, Math.min(255, x)); }

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const h = (n) => clamp(Math.round(n)).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

function shade(hex, pct) {
  const { r, g, b } = hexToRgb(hex);
  const mix = (c) => (pct >= 0 ? c + (255 - c) * pct : c * (1 + pct));
  return rgbToHex({ r: mix(r), g: mix(g), b: mix(b) });
}

function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const srgb = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function foregroundOn(hex) {
  // WCAG-ish: dark text on light brand, white on dark brand.
  // Threshold 0.179 keeps the default green and yellow with dark text (readable).
  return relativeLuminance(hex) > 0.179 ? "#0B110F" : "#FFFFFF";
}

function rgbChannel(hex) {
  const { r, g, b } = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

export function applyBrandColor(hex) {
  const base = hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex.toUpperCase() : DEFAULT_BRAND;
  const dark = shade(base, -0.25);
  const neon = shade(base, 0.2);
  const fg = foregroundOn(base);
  const root = document.documentElement.style;
  root.setProperty("--c-brand", base);
  root.setProperty("--c-brand-dark", dark);
  root.setProperty("--c-brand-neon", neon);
  root.setProperty("--c-brand-fg", fg);
  root.setProperty("--c-brand-rgb", rgbChannel(base));
  root.setProperty("--c-brand-dark-rgb", rgbChannel(dark));
  root.setProperty("--c-brand-neon-rgb", rgbChannel(neon));
}

export function BrandTheme() {
  const { user } = useAuth();
  const brand = user?.shop?.brand_color || DEFAULT_BRAND;
  useEffect(() => { applyBrandColor(brand); }, [brand]);
  return null;
}

export const BRAND_PRESETS = [
  { label: "Verde (padrão)", value: "#22C55E" },
  { label: "Azul", value: "#2563EB" },
  { label: "Vermelho", value: "#DC2626" },
  { label: "Laranja", value: "#EA580C" },
  { label: "Roxo", value: "#7C3AED" },
  { label: "Rosa", value: "#DB2777" },
  { label: "Amarelo", value: "#EAB308" },
  { label: "Grafite", value: "#334155" },
];

export const DEFAULT_BRAND_COLOR = DEFAULT_BRAND;
